import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, MapPin, DollarSign, Car, Info, Check, Zap, Award, PartyPopper, AlertTriangle, FileText, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Navbar } from '@/components/Navbar';
import { MapComponent } from '@/components/MapComponent';
import { PhotoUploader } from '@/components/PhotoUploader';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showCongrats, setShowCongrats] = useState(false);
  const [congratsData, setCongratsData] = useState({ isPriority: false, isEarlyHost: false });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    suburb: '',
    postcode: '',
    latitude: -33.8688,
    longitude: 151.2093,
    price_hourly: '',
    price_daily: '',
    vehicle_sizes: ['small', 'medium'],
    access_instructions: '',
    photos: []
  });

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);

  // Verification document state
  const [verificationDocs, setVerificationDocs] = useState({ proof: null, id: null });
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'proof' | 'id' | null
  const [createdListingId, setCreatedListingId] = useState(null);
  const [confirmRightToRent, setConfirmRightToRent] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // NOTE: Bank/Stripe setup is no longer required to publish. Earnings accumulate in wallet; hosts connect a bank account later to withdraw.

  useEffect(() => {
    if (id) {
      // Edit mode - fetch existing listing
      axios.get(`${API}/listings/${id}`, { withCredentials: true, headers: getAuthHeaders() })
        .then(res => {
          const listing = res.data;
          setFormData({
            title: listing.title,
            description: listing.description || '',
            address: listing.address,
            suburb: listing.suburb,
            postcode: listing.postcode,
            latitude: listing.location?.coordinates?.[1] || -33.8688,
            longitude: listing.location?.coordinates?.[0] || 151.2093,
            price_hourly: listing.price_hourly,
            price_daily: listing.price_daily || '',
            vehicle_sizes: listing.vehicle_sizes || ['medium'],
            access_instructions: listing.access_instructions || '',
            photos: listing.photos || []
          });
        })
        .catch(() => {
          toast.error('Listing not found');
          navigate('/host');
        });
    }
  }, [id, navigate]);

  const handleAddressSearch = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setSearchingAddress(true);
    try {
      const res = await axios.post(`${API}/geocode/forward?address=${encodeURIComponent(query)}`);
      setAddressSuggestions(res.data.results || []);
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleSelectAddress = (result) => {
    setFormData(f => ({
      ...f,
      address: result.address,
      suburb: result.suburb || '',
      postcode: result.postcode || '',
      latitude: result.coordinates.lat,
      longitude: result.coordinates.lng
    }));
    setAddressSuggestions([]);
  };

  const handleVehicleSizeToggle = (size) => {
    setFormData(f => ({
      ...f,
      vehicle_sizes: f.vehicle_sizes.includes(size)
        ? f.vehicle_sizes.filter(s => s !== size)
        : [...f.vehicle_sizes, size]
    }));
  };

  const handlePhotosChange = (newPhotos) => {
    setFormData(f => ({ ...f, photos: newPhotos }));
  };

  const handleSubmit = async (publish = false) => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...formData,
        price_hourly: parseFloat(formData.price_hourly),
        price_daily: formData.price_daily ? parseFloat(formData.price_daily) : null,
        right_to_rent_confirmed: confirmRightToRent,
        status: publish ? 'published' : 'draft',
      };

      if (id) {
        await axios.put(`${API}/listings/${id}`, payload, { withCredentials: true, headers });
        toast.success(publish ? 'Listing published!' : 'Listing updated!');
        navigate('/host');
      } else {
        // Single atomic POST — no race condition between create and status update
        const res = await axios.post(`${API}/listings`, payload, { withCredentials: true, headers });
        
        const isPriority = res.data.is_priority_listing;
        const isEarlyHost = res.data.is_early_adopter || user?.is_early_adopter;
        
        if (isPriority || isEarlyHost) {
          setCongratsData({ isPriority, isEarlyHost });
          setShowCongrats(true);
        } else {
          toast.success(publish ? 'Listing published!' : 'Draft saved!');
          navigate('/host');
        }
      }
    } catch (error) {
      console.error('Error saving listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    } finally {
      setLoading(false);
    }
  };

  const handleDocUpload = async (file, docType) => {
    const listingId = createdListingId || id;
    if (!listingId || !file) return;

    setUploadingDoc(docType);
    try {
      const headers = getAuthHeaders();
      const formPayload = new FormData();
      formPayload.append('file', file);

      const res = await axios.post(
        `${API}/verification/upload?listing_id=${listingId}&document_type=${docType}`,
        formPayload,
        { withCredentials: true, headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );

      setVerificationDocs(prev => ({
        ...prev,
        [docType === 'proof_of_ownership' ? 'proof' : 'id']: {
          name: file.name,
          docId: res.data.doc_id
        }
      }));
      toast.success(`${docType === 'proof_of_ownership' ? 'Ownership document' : 'Photo ID'} uploaded!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmitVerification = async () => {
    const listingId = createdListingId || id;
    if (!listingId) return;

    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/verification/submit/${listingId}`, {}, { withCredentials: true, headers });
      
      if (congratsData.isPriority || congratsData.isEarlyHost) {
        setShowCongrats(true);
      } else {
        toast.success('Documents submitted! Your listing is under review.');
        navigate('/host');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const handleCongratsClose = () => {
    setShowCongrats(false);
    navigate('/host');
  };

  const isStep1Valid = formData.title && formData.address && formData.suburb;
  const isStep2Valid = formData.price_hourly && formData.vehicle_sizes.length > 0;
  const isStep3Valid = true; // Photos optional
  const totalSteps = 3;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      {/* Congratulations Dialog */}
      <Dialog open={showCongrats} onOpenChange={setShowCongrats}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
              <PartyPopper className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold">
              Congratulations! 🎉
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {congratsData.isPriority && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-700">Priority Listing!</span>
                </div>
                <p className="text-sm text-purple-600">
                  Your driveway is one of the first 3,000 listed on SpotFinder! 
                  It will appear higher in search results with a special badge.
                </p>
              </div>
            )}
            
            {congratsData.isEarlyHost && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-700">Early Host!</span>
                </div>
                <p className="text-sm text-amber-600">
                  You're one of SpotFinder's first 1,000 hosts! 
                  You'll have a special Early Host badge on all your listings.
                </p>
              </div>
            )}
            
            <p className="text-slate-600 text-sm">
              Thank you for being part of our community!
            </p>
          </div>
          
          <Button 
            onClick={handleCongratsClose} 
            className="w-full bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
          >
            View My Listing
          </Button>
        </DialogContent>
      </Dialog>

      <div className="pt-20 sm:pt-24 pb-8 sm:pb-12 px-4 sm:px-0">
        <div className="container-main max-w-3xl">
          {/* Header */}
          <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Button variant="ghost" size="icon" onClick={() => navigate('/host')} className="rounded-full shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                {id ? 'Edit Listing' : 'Create New Listing'}
              </h1>
              <p className="text-sm text-slate-500">Step {step} of {totalSteps}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-6 sm:mb-8">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => (
              <div key={s} className="flex-1">
                <div className={`h-1.5 sm:h-2 rounded-full ${s <= step ? 'bg-[#5ebc67]' : 'bg-slate-200'}`}></div>
              </div>
            ))}
          </div>

          {/* Step 1: Location */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#5ebc67]" />
                  Location Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Listing Title</Label>
                  <Input
                    placeholder="e.g., Secure Driveway near CBD"
                    value={formData.title}
                    onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                    data-testid="listing-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <div className="relative">
                    <Input
                      placeholder="Search for your address..."
                      value={formData.address}
                      onChange={(e) => {
                        setFormData(f => ({ ...f, address: e.target.value }));
                        handleAddressSearch(e.target.value);
                      }}
                      data-testid="listing-address-input"
                    />
                    {addressSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-10 mt-1">
                        {addressSuggestions.map((result, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleSelectAddress(result)}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b last:border-0"
                          >
                            {result.address}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Suburb</Label>
                    <Input
                      placeholder="Suburb"
                      value={formData.suburb}
                      onChange={(e) => setFormData(f => ({ ...f, suburb: e.target.value }))}
                      data-testid="listing-suburb-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      placeholder="2000"
                      value={formData.postcode}
                      onChange={(e) => setFormData(f => ({ ...f, postcode: e.target.value }))}
                      data-testid="listing-postcode-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Describe your parking spot..."
                    value={formData.description}
                    onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                    rows={4}
                    data-testid="listing-description-input"
                  />
                </div>

                {/* Map Preview */}
                <div className="h-64 rounded-xl overflow-hidden">
                  <MapComponent
                    listings={[{
                      listing_id: 'preview',
                      location: { coordinates: [formData.longitude, formData.latitude] },
                      price_hourly: formData.price_hourly || 10
                    }]}
                    center={[formData.longitude, formData.latitude]}
                    zoom={15}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!isStep1Valid}
                    className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
                    data-testid="next-step-btn"
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Pricing */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#5ebc67]" />
                  Pricing & Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      placeholder="5"
                      value={formData.price_hourly}
                      onChange={(e) => setFormData(f => ({ ...f, price_hourly: e.target.value }))}
                      data-testid="price-hourly-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Daily Rate ($ - optional)</Label>
                    <Input
                      type="number"
                      placeholder="30"
                      value={formData.price_daily}
                      onChange={(e) => setFormData(f => ({ ...f, price_daily: e.target.value }))}
                      data-testid="price-daily-input"
                    />
                    <p className="text-xs text-slate-500">Applied for 8+ hours</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehicle Sizes Allowed
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {['small', 'medium', 'large', 'oversized'].map(size => (
                      <label
                        key={size}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                          formData.vehicle_sizes.includes(size)
                            ? 'border-[#5ebc67] bg-green-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.vehicle_sizes.includes(size)}
                          onCheckedChange={() => handleVehicleSizeToggle(size)}
                        />
                        <span className="capitalize">{size}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Access Instructions (shown after booking)
                  </Label>
                  <Textarea
                    placeholder="e.g., Gate code is 1234. Park on the left side..."
                    value={formData.access_instructions}
                    onChange={(e) => setFormData(f => ({ ...f, access_instructions: e.target.value }))}
                    rows={3}
                    data-testid="access-instructions-input"
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-full w-full sm:w-auto">
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!isStep2Valid}
                    className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full w-full sm:w-auto"
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Photos */}
          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#5ebc67]" />
                  Photos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <PhotoUploader
                  listingId={id}
                  photos={formData.photos}
                  onPhotosChange={handlePhotosChange}
                  maxPhotos={5}
                />

                {/* Right to Rent Confirmation */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200 mt-4">
                  <Checkbox
                    id="right-to-rent"
                    checked={confirmRightToRent}
                    onCheckedChange={(checked) => setConfirmRightToRent(!!checked)}
                    className="mt-0.5"
                    data-testid="right-to-rent-checkbox"
                  />
                  <Label htmlFor="right-to-rent" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    I confirm I have the right to rent this space
                  </Label>
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
                  <Button variant="outline" onClick={() => setStep(2)} className="rounded-full w-full sm:w-auto">
                    Back
                  </Button>
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      onClick={() => handleSubmit(false)}
                      disabled={loading}
                      className="rounded-full w-full sm:w-auto"
                      data-testid="save-draft-btn"
                    >
                      Save as Draft
                    </Button>
                    <Button
                      onClick={() => handleSubmit(true)}
                      disabled={loading || !confirmRightToRent}
                      className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full w-full sm:w-auto"
                      data-testid="publish-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {loading ? 'Publishing...' : 'Publish Listing'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
};

export default CreateListingPage;
